import { GAMMA_SHORT_CHAIN } from "./gamma";
import { summariseDailyQuality } from "./hourly-windows";
/**
 * Open Water Swimming Scoring V2
 *
 * Purely multiplicative experience-quality score.
 *   hourly = BASE * F_chop * F_wind * F_current * F_weather * F_waterQuality
 *
 * Five independent factors, each a continuous curve with no hard caps.
 * Safety is a separate, independent output (computeSwimSafety) displayed
 * alongside the score, never modifying it.
 *
 * Design decisions:
 *  - Water temperature is NOT in the score. It creates permanent geographic
 *    bias (warm locations always outscore cold ones). Locals dress for their
 *    climate. Temperature is surfaced via the temperature tag, cold-shock
 *    safety indicator, and gear recommendation instead.
 *  - F_weather combines sunshine, rain, and fog into one factor
 *    to avoid over-compounding from many small penalties.
 *  - Air temperature excluded from score (same logic as water temperature:
 *    locals dress for their climate, and including it creates permanent
 *    geographic bias). Surfaced via safety indicators instead.
 *  - F_waterQuality combines secchi depth, chlorophyll-a, SPM, and bathing
 *    classification into one factor via arithmetic mean of sub-scores.
 *  - Swell period adjusts the chop calculation: long-period swell produces
 *    gentle rollers, not disruptive chop.
 *  - UV removed from quality score (health indicator, not experience).
 *  - Thunderstorms, CSO, extreme current/waves -> safety indicators only.
 *  - Daily rollup: 40% daylight average + 60% best 3-hour window.
 *    Swimmers pick their window; the best hours dominate.
 */

import type { SunTimes } from "./daylight";
import { isDark } from "./daylight";
import { composeScore, defineChain } from "./compose-score";
import { classifyModelFog } from "./model-fog";

// Swim's chain, declared as data (tracker P3-19); classes declared, not enforced (Phase 7).
// Gamma derived from the 5 declared factors (SHORT) under the Phase 6
// all-or-none policy (tracker P6-1, audit 90.3 #2). The BASE_SCORE 10.0 scale
// constant is applied outside composeScore so gamma compresses the 0-1 quality
// product, matching every other chain.
export const SWIM_CHAIN = defineChain({
  sport: "swim",
  gamma: GAMMA_SHORT_CHAIN, // Pinned calibration; factor-list refactors must not change the scale.
  factors: [
    { key: "chop", class: "driver" },
    { key: "wind", class: "driver" },
    { key: "current", class: "driver" },
    { key: "weather", class: "modifier", note: "P7-4 (audit 08 V1): the sun x rain x fog composite floors as one enjoyment modifier (a glassy drizzle swim is still a top-tier swim; you are wet either way), delivering the deferred P7-3 swim rain floor and the fog floor together. computeSwimSafety already carries the paired sub-300m red + sub-1000m amber fog flag (08 B1, P7-5). Max composite is 1.04 (clear-sky sun bonus), within the modifier ceiling; floor is internal to composeScore, so factors.weather surfaces the raw composite" },
    { key: "waterQuality", class: "driver", note: "medium-degrading (CSO override); P7-13 defuses the bathing-classification multiplier" },
  ],
});

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * Math.min(1, Math.max(0, t));
}

function clamp(val: number, min: number, max: number): number {
  return Math.min(Math.max(val, min), max);
}

function piecewise(x: number, points: [number, number][]): number {
  if (points.length === 0) return 1.0;
  if (x <= points[0][0]) return points[0][1];
  for (let i = 1; i < points.length; i++) {
    if (x <= points[i][0]) {
      const [x0, y0] = points[i - 1];
      const [x1, y1] = points[i];
      return lerp(y0, y1, (x - x0) / (x1 - x0));
    }
  }
  return points[points.length - 1][1];
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type SwimHourlyInput = {
  swellHeight: number;
  windWaveHeight: number | null;
  swellPeriod: number | null;
  windSpeed: number;
  windGusts: number;
  currentVelocity: number;
  waterTempC: number | null;
  airApparentTempC: number | null;
  cloudCoverPct: number | null;
  secchiDepthM: number | null;
  chlorophyllA: number | null;
  spm: number | null;
  bathingClassification: string | null;
  precipitation: number;
  trailingRain24h: number;
  visibilityM: number;
  weatherCode: number;
  capeJkg: number;
  uvIndex: number;
  csoActive: boolean | null;
  hasCsoData: boolean;
  hour: number;
  sunrise: number;
  sunset: number;
  shelterFactor?: number;
};

export type SwimFactors = {
  chop: number;
  wind: number;
  current: number;
  weather: number;
  waterQuality: number;
};

export type SwimHourlyResult = {
  score: number;
  label: string;
  factors: SwimFactors;
  temperatureTag: string;
  temperatureC: number | null;
  effectiveChop: number;
  chopConfidence: "full" | "inferred";
  safety: SwimSafetyResult;
};

export type SafetySeverity = "green" | "amber" | "red";

export type SafetyIndicator = {
  key: string;
  severity: SafetySeverity | "unavailable";
  label: string;
  detail: string;
  confidence?: "full" | "unavailable";
  provenance?: "missing-forecast-input";
};

export type SwimSafetyResult = {
  overall: SafetySeverity;
  indicators: SafetyIndicator[];
};

export type SwimTemperatureTag = "Ice" | "Cold" | "Cool" | "Mild" | "Warm" | "Tropical";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const BASE_SCORE = 10.0;

// Chop: swell period adjustment -- long-period swell is gentler for swimmers
const PERIOD_ADJUSTMENT: [number, number][] = [
  [0, 0.55],
  [6, 0.50],
  [8, 0.35],
  [12, 0.25],
  [16, 0.20],
];

// Short chop interrupts breathing and stroke rhythm. Keep sheltered ripples
// gentle, with a stronger decline once the surface becomes persistently rough.
const CHOP_CURVE: [number, number][] = [
  [0.0, 1.0],
  [0.1, 0.97],
  [0.2, 0.935],
  [0.3, 0.80],
  [0.5, 0.55],
  [0.8, 0.20],
  [1.0, 0.12],
  [1.2, 0.07],
  [1.8, 0.025],
  [2.5, 0.01],
];

// Wind: sustained speed (km/h) -> factor
const WIND_CURVE: [number, number][] = [
  [0, 1.0],
  [5, 1.0],
  [10, 0.93],
  [15, 0.83],
  [20, 0.70],
  [25, 0.55],
  [30, 0.40],
  [40, 0.18],
  [50, 0.08],
];

// Gust ratio penalty: ratio -> multiplier on wind factor
const GUST_RATIO_THRESHOLD = 1.5;
const GUST_PENALTY_RATE = 0.10;
const GUST_PENALTY_FLOOR = 0.85;

// Audit 08 V3: WIND_CURVE is sized as a full wind-sea proxy for the FORECAST path,
// where no measured wind-wave height prices what the wind did to the sea. When
// wind-wave height IS measured, swimChopFactor already carries that effect, so the
// full curve would double-count it. The residual non-chop effect of wind (spray,
// breathing-side chop) is an enjoyment-scale nuisance, so on the measured path the
// wind term floors at this residual instead of running to the 0.40/0.08 tail. The
// canonical enjoyment-modifier floor (compose-score MODIFIER_FLOOR).
const MEASURED_CHOP_WIND_FLOOR = 0.85;

// Current: a swim returning to its starting point, rather than a drift swim.
// Above 0.5 m/s, return progress keeps deteriorating instead of plateauing at
// moderate effort. These experience anchors do not assume a personal swim speed.
const CURRENT_CURVE: [number, number][] = [
  [0.0, 1.0],
  [0.05, 1.0],
  [0.10, 0.97],
  [0.15, 0.93],
  [0.20, 0.87],
  [0.30, 0.72],
  [0.40, 0.55],
  [0.50, 0.40],
  [0.75, 0.20],
  [1.0, 0.08],
  [1.5, 0.02],
  [2.0, 0.005],
];

// Water temperature is deliberately NOT scored (wetsuits solve it; scoring it
// would permanently bias against cold-water regions). It is surfaced via the
// temperature tag and gear recommendations instead.

// Sunshine: cloud_cover_pct -> sub-factor
const SUN_CURVE: [number, number][] = [
  [0, 1.04],
  [10, 1.04],
  [30, 1.01],
  [50, 0.95],
  [70, 0.90],
  [85, 0.86],
  [100, 0.83],
];

// Rain: hourly precipitation (mm) -> sub-factor
const RAIN_CURVE: [number, number][] = [
  [0, 1.0],
  [0.5, 0.97],
  [1.0, 0.94],
  [2.0, 0.88],
  [4.0, 0.80],
  [8.0, 0.72],
];

// Fog: atmospheric visibility (m) -> sub-factor
const FOG_CURVE: [number, number][] = [
  [200, 0.50],
  [500, 0.65],
  [1000, 0.80],
  [2000, 0.92],
  [5000, 0.98],
  [10000, 1.0],
];

// Sewage proxy: trailing 24h rain when no CSO data.
// P7-17/08 B3 (rain + WQ same-front sweep): DECIDED NO CHANGE. Rain does appear
// twice in the swim chain - the in-window RAIN_CURVE in F_weather and this trailing
// 24h proxy in F_waterQuality - but they are distinct proxies on distinct timescales
// and inputs: RAIN_CURVE prices rain falling NOW (visible-water comfort, the swimmer
// is wet either way), this proxy prices the antecedent 24-72h runoff/sewage HEALTH
// risk after heavy rain (the genuine expert behaviour change). The audit files 08 B3
// as a borderline, not a violation, and treats contamination as a real distinct
// medium effect, so the two doors are kept separate.
const SEWAGE_PROXY_CURVE: [number, number][] = [
  [0, 1.0],
  [5, 1.0],
  [12, 0.90],
  [25, 0.72],
  [40, 0.55],
];

// Secchi depth -> sub-score
const SECCHI_CURVE: [number, number][] = [
  [0, 0.60],
  [0.5, 0.72],
  [1.0, 0.82],
  [2.0, 0.90],
  [4.0, 0.96],
  [6.0, 0.99],
  [8.0, 1.0],
];

// Chlorophyll-a (mg/m3) -> sub-score
const CHLOROPHYLL_CURVE: [number, number][] = [
  [0, 1.0],
  [3, 1.0],
  [8, 0.90],
  [15, 0.75],
  [25, 0.55],
  [40, 0.40],
];

// SPM (g/m3) -> sub-score
const SPM_CURVE: [number, number][] = [
  [0, 1.0],
  [3, 1.0],
  [10, 0.90],
  [20, 0.78],
  [35, 0.65],
  [50, 0.55],
];

// Daily rollup weights
const DAILY_BEST_WINDOW_WEIGHT = 0.60;
const DAILY_BEST_WINDOW_HOURS = 3;

// Safety thresholds
const SAFETY_CURRENT_AMBER_MS = 0.3;
const SAFETY_CURRENT_RED_MS = 0.6;
const SAFETY_WAVE_AMBER_M = 1.0;
const SAFETY_WAVE_RED_M = 2.0;
const SAFETY_WIND_AMBER_KMH = 30;
const SAFETY_WIND_RED_KMH = 45;
const SAFETY_GUST_AMBER_KMH = 40;
const SAFETY_GUST_RED_KMH = 60;
const SAFETY_VIS_AMBER_M = 1000;
const SAFETY_VIS_RED_M = 300;
const SAFETY_UV_AMBER = 6;
const SAFETY_UV_RED = 8;
const SAFETY_COLD_WATER_C = 15;
const SAFETY_COLD_SHOCK_C = 10;
const SAFETY_CHLOROPHYLL_BLOOM = 20;
const SAFETY_CAPE_RED = 1000;
const SAFETY_CAPE_AMBER = 500;
const THUNDERSTORM_CODES = new Set([95, 96, 99]);
const FOG_CODES = new Set([45, 48]);
const CONVECTIVE_RAIN_CODES = new Set([61, 63, 65, 80, 81, 82]);

// ---------------------------------------------------------------------------
// Labels
// ---------------------------------------------------------------------------

export function swimLabel(score: number): string {
  if (score < 2.0) return "Flat";
  if (score < 3.5) return "Poor";
  if (score < 5.0) return "Fair";
  if (score < 6.5) return "Good";
  if (score < 7.5) return "Great";
  if (score < 9.0) return "Firing";
  return "Hero";
}

export function swimTemperatureTag(tempC: number): SwimTemperatureTag {
  if (tempC < 6) return "Ice";
  if (tempC < 11) return "Cold";
  if (tempC < 16) return "Cool";
  if (tempC < 20) return "Mild";
  if (tempC < 24) return "Warm";
  return "Tropical";
}

// ---------------------------------------------------------------------------
// Factor 1: Chop (surface wave state)
// ---------------------------------------------------------------------------

export function swimEffectiveChop(
  swellHeight: number,
  windWaveHeight: number | null,
  swellPeriod: number | null = null
): number {
  const periodAdj = piecewise(swellPeriod ?? 8, PERIOD_ADJUSTMENT);
  const swellContrib = Math.max(0, swellHeight - 0.3) * periodAdj;
  if (windWaveHeight !== null) {
    return windWaveHeight + swellContrib;
  }
  // No wind-wave reading: apply the same period adjustment to the swell
  // rather than treating the raw swell height as chop. Previously a 1.2m
  // 14s groundswell scored 2.0 with windWaveHeight null but 9.5 with
  // windWaveHeight 0 -- identical ocean, 7.5-point swing on data availability.
  return swellContrib;
}

export function swimChopFactor(chop: number): number {
  return piecewise(chop, CHOP_CURVE);
}

// ---------------------------------------------------------------------------
// Factor 2: Wind (sustained + gust consistency)
// ---------------------------------------------------------------------------

export function swimWindFactor(
  windSpeedKmh: number,
  gustsKmh: number,
  chopMeasured = false
): number {
  const base = piecewise(windSpeedKmh, WIND_CURVE);
  const gustRatio = gustsKmh / Math.max(windSpeedKmh, 1);
  let gustAdj = 1.0;
  if (gustRatio > GUST_RATIO_THRESHOLD) {
    gustAdj = Math.max(
      GUST_PENALTY_FLOOR,
      1.0 - GUST_PENALTY_RATE * (gustRatio - GUST_RATIO_THRESHOLD)
    );
  }
  const factor = base * gustAdj;
  // Audit 08 V3: when chop is measured, swimChopFactor already prices the wind's
  // effect on the water, so floor the wind term at the residual non-chop nuisance
  // (the floor only binds at strong winds; light winds keep their honest penalty).
  // The unmeasured/forecast path keeps the full WIND_CURVE proxy unchanged.
  return chopMeasured ? Math.max(MEASURED_CHOP_WIND_FLOOR, factor) : factor;
}

// ---------------------------------------------------------------------------
// Factor 3: Current (effort and progress on a return swim)
// ---------------------------------------------------------------------------

export function swimCurrentFactor(velocityMs: number): number {
  return piecewise(velocityMs, CURRENT_CURVE);
}

// ---------------------------------------------------------------------------
// Factor 4: Weather (sunshine + rain + fog + air comfort)
// ---------------------------------------------------------------------------

export function swimWeatherFactor(input: {
  cloudCoverPct: number | null;
  precipitationMm: number;
  visibilityM: number;
  airApparentTempC: number | null;
  weatherCode: number;
}): number {
  const sunSub = piecewise(input.cloudCoverPct ?? 50, SUN_CURVE);
  const rainSub = piecewise(input.precipitationMm, RAIN_CURVE);

  let fogSub = piecewise(input.visibilityM, FOG_CURVE);
  if (FOG_CODES.has(input.weatherCode) && fogSub > 0.85) {
    fogSub = 0.85;
  }

  return sunSub * rainSub * fogSub;
}

// ---------------------------------------------------------------------------
// Factor 5: Water quality (secchi + chlorophyll + spm + bathing + sewage)
// ---------------------------------------------------------------------------

export function swimWaterQualityFactor(input: {
  secchiDepthM: number | null;
  chlorophyllA: number | null;
  spm: number | null;
  bathingClassification: string | null;
  csoActive: boolean | null;
  hasCsoData: boolean;
  trailingRain24h: number;
}): number {
  if (input.csoActive === true) return 0.25;

  const secchiSub = piecewise(input.secchiDepthM ?? 4.5, SECCHI_CURVE);
  const chlaSub = piecewise(input.chlorophyllA ?? 4.0, CHLOROPHYLL_CURVE);
  const spmSub = piecewise(input.spm ?? 5.0, SPM_CURVE);
  const avgSub = (secchiSub + chlaSub + spmSub) / 3;

  // P7-13 (audit 08 #4): the EA annual bathing-water classification is NOT
  // applied to the score. An annual rating cannot vary across a decision window,
  // so wiring it in would impose the permanent geographic bias the water-temp
  // exclusion exists to prevent (a "Poor" spot docked 40% every day forever), and
  // the capitalised-key map was a silent case-sensitivity trap waiting on a future
  // EA feed. Day-varying contamination is priced by the CSO override (above) and
  // the trailing-rain sewage proxy (below); the annual class stays a flag-layer
  // concern only (diving already renders it as a red/amber safety flag).
  // `bathingClassification` is retained in the input for that flag path but is
  // deliberately not a score multiplier.
  let sewageSub = 1.0;
  if (!input.hasCsoData) {
    sewageSub = piecewise(input.trailingRain24h, SEWAGE_PROXY_CURVE);
  }

  return avgSub * sewageSub;
}

// ---------------------------------------------------------------------------
// Safety (independent of score)
// ---------------------------------------------------------------------------

function worstSeverity(a: SafetySeverity, b: SafetySeverity): SafetySeverity {
  const rank: Record<SafetySeverity, number> = { green: 0, amber: 1, red: 2 };
  return rank[a] >= rank[b] ? a : b;
}

export function computeSwimSafety(input: {
  currentVelocity: number | null;
  windSpeed: number;
  windGusts: number | null;
  significantWaveHeight: number | null;
  visibilityM: number | null;
  uvIndex: number;
  weatherCode: number;
  capeJkg: number;
  waterTempC: number | null;
  csoActive: boolean | null;
  chlorophyllA: number | null;
  hasCsoData: boolean;
  trailingRain24h: number;
}): SwimSafetyResult {
  const indicators: SafetyIndicator[] = [];

  if (input.currentVelocity === null) {
    indicators.push({ key: "current_unavailable", severity: "unavailable", label: "Current data unavailable", detail: "The current forecast is missing, so drift risk cannot be assessed.", confidence: "unavailable", provenance: "missing-forecast-input" });
  } else if (input.currentVelocity > SAFETY_CURRENT_RED_MS) {
    indicators.push({
      key: "current_extreme",
      severity: "red",
      label: "Strong current",
      detail: `Current ${input.currentVelocity.toFixed(1)} m/s. Risk of being swept into shipping lanes or out to sea. Do not swim.`,
    });
  } else if (input.currentVelocity > SAFETY_CURRENT_AMBER_MS) {
    indicators.push({
      key: "current_moderate",
      severity: "amber",
      label: "Moderate current",
      detail: `Current ${input.currentVelocity.toFixed(1)} m/s. Swim parallel to shore and stay within your depth.`,
    });
  }

  if (input.significantWaveHeight === null) {
    indicators.push({ key: "wave_unavailable", severity: "unavailable", label: "Sea-state data unavailable", detail: "The wind-wave forecast is missing, so significant wave height cannot be assessed.", confidence: "unavailable", provenance: "missing-forecast-input" });
  } else if (input.significantWaveHeight > SAFETY_WAVE_RED_M) {
    indicators.push({
      key: "wave_extreme",
      severity: "red",
      label: "Dangerous waves",
      detail: `Wave height ${input.significantWaveHeight.toFixed(1)}m. Not safe for open water swimming.`,
    });
  } else if (input.significantWaveHeight > SAFETY_WAVE_AMBER_M) {
    indicators.push({
      key: "wave_large",
      severity: "amber",
      label: "Large waves",
      detail: `Wave height ${input.significantWaveHeight.toFixed(1)}m. Experienced swimmers only. Consider sheltered alternatives.`,
    });
  }

  if (input.windGusts === null) {
    indicators.push({ key: "gust_unavailable", severity: "unavailable", label: "Gust data unavailable", detail: "The gust forecast is missing, so offshore push cannot be assessed.", confidence: "unavailable", provenance: "missing-forecast-input" });
  } else if (input.windGusts > SAFETY_GUST_RED_KMH) {
    indicators.push({
      key: "gust_extreme",
      severity: "red",
      label: "Dangerous gusts",
      detail: `Gusts ${Math.round(input.windGusts)} km/h. Risk of being pushed offshore.`,
    });
  } else if (input.windGusts > SAFETY_GUST_AMBER_KMH || input.windSpeed > SAFETY_WIND_RED_KMH) {
    indicators.push({
      key: "wind_strong",
      severity: "amber",
      label: "Strong wind",
      detail: `Wind ${Math.round(input.windSpeed)} km/h with gusts to ${Math.round(input.windGusts)} km/h. Stay close to shore.`,
    });
  } else if (input.windSpeed > SAFETY_WIND_AMBER_KMH) {
    indicators.push({
      key: "wind_moderate",
      severity: "amber",
      label: "Strong wind",
      detail: `Wind ${Math.round(input.windSpeed)} km/h. Consider sheltered alternatives.`,
    });
  }

  if (input.visibilityM === null) {
    indicators.push({ key: "visibility_unavailable", severity: "unavailable", label: "Visibility data unavailable", detail: "The visibility forecast is missing, so sighting and support-craft awareness cannot be assessed.", confidence: "unavailable", provenance: "missing-forecast-input" });
  } else if (input.visibilityM < SAFETY_VIS_RED_M) {
    indicators.push({
      key: "visibility_extreme",
      severity: "red",
      label: "Dense fog",
      detail: `Visibility ${Math.round(input.visibilityM)}m. Cannot see shore, buoys, or support craft. Do not swim.`,
    });
  } else if (input.visibilityM < SAFETY_VIS_AMBER_M) {
    indicators.push({
      key: "visibility_reduced",
      severity: "amber",
      label: "Reduced visibility",
      detail: `Visibility ${Math.round(input.visibilityM)}m. Stay close to shore and swim with a buddy.`,
    });
  }

  if (THUNDERSTORM_CODES.has(input.weatherCode)) {
    indicators.push({
      key: "lightning",
      severity: "red",
      label: "Thunderstorm",
      detail: "Lightning is extremely dangerous in open water. Do not swim. Seek shelter immediately.",
    });
  } else if (input.capeJkg > SAFETY_CAPE_RED) {
    indicators.push({
      key: "lightning_risk",
      severity: "red",
      label: "Thunderstorm risk",
      detail: "High convective energy indicates thunderstorm risk. Monitor conditions and stay close to exit points.",
    });
  } else if (input.capeJkg > SAFETY_CAPE_AMBER && CONVECTIVE_RAIN_CODES.has(input.weatherCode)) {
    indicators.push({
      key: "lightning_possible",
      severity: "amber",
      label: "Thunderstorms possible",
      detail: "Convective rain with moderate instability. Stay close to shore and monitor conditions.",
    });
  }

  if (input.csoActive === true) {
    indicators.push({
      key: "sewage",
      severity: "red",
      label: "Sewage alert",
      detail: "Combined sewer overflow active. Avoid swimming. Check local water authority for updates.",
    });
  }

  const chla = input.chlorophyllA ?? 0;
  if (chla > 30) {
    indicators.push({
      key: "algae_severe",
      severity: "red",
      label: "Algae bloom",
      detail: "Very high chlorophyll levels indicate harmful algal bloom risk. Avoid water contact.",
    });
  } else if (chla > SAFETY_CHLOROPHYLL_BLOOM) {
    indicators.push({
      key: "algae_elevated",
      severity: "amber",
      label: "Algae bloom risk",
      detail: "Elevated chlorophyll levels. Monitor local water quality advisories.",
    });
  }

  if (input.uvIndex > SAFETY_UV_RED) {
    indicators.push({
      key: "uv_very_high",
      severity: "amber",
      label: `High UV (${Math.round(input.uvIndex)})`,
      detail: "Extended open water exposure with no shade. Wear SPF 50+ waterproof sunscreen.",
    });
  } else if (input.uvIndex > SAFETY_UV_AMBER) {
    indicators.push({
      key: "uv_high",
      severity: "amber",
      label: `Moderate UV (${Math.round(input.uvIndex)})`,
      detail: "Apply waterproof sunscreen regularly.",
    });
  }

  if (input.waterTempC !== null && input.waterTempC < SAFETY_COLD_WATER_C) {
    const extremeCold = input.waterTempC < 5;
    const coldShock = input.waterTempC < SAFETY_COLD_SHOCK_C;
    indicators.push({
      key: "cold_shock",
      severity: extremeCold ? "red" : "amber",
      label: extremeCold
        ? "Extreme cold water"
        : coldShock
          ? "Cold water shock risk"
          : "Cold water guidance",
      detail: extremeCold
        ? `Water ${Math.round(input.waterTempC)}C. Hypothermia risk even with wetsuit. Experienced cold water swimmers only.`
        : coldShock
          ? `Water ${Math.round(input.waterTempC)}C. Risk of cold water shock on entry. Acclimatise gradually and wear appropriate neoprene.`
          : `Water ${Math.round(input.waterTempC)}C. Cold-water exposure starts below 15C. Acclimatise gradually, use suitable kit and plan a warm exit.`,
    });
  }

  if (!input.hasCsoData && input.trailingRain24h > 12) {
    indicators.push({
      key: "rainfall_runoff",
      severity: "amber",
      label: "Rainfall warning",
      detail: `${Math.round(input.trailingRain24h)}mm rain in 24h. Possible elevated bacteria from runoff and overflows.`,
    });
  }

  let overall: SafetySeverity = "green";
  for (const ind of indicators) {
    if (ind.severity !== "unavailable") overall = worstSeverity(overall, ind.severity);
  }

  return { overall, indicators };
}

// ---------------------------------------------------------------------------
// Main hourly scoring
// ---------------------------------------------------------------------------

export function calculateSwimHourly(input: SwimHourlyInput): SwimHourlyResult {
  const shelter = input.shelterFactor ?? 1.0;
  const effectiveSwell = input.swellHeight * shelter;
  const effectiveWindWave = input.windWaveHeight !== null ? input.windWaveHeight * shelter : null;

  const chop = swimEffectiveChop(effectiveSwell, effectiveWindWave, input.swellPeriod);
  const fChop = swimChopFactor(chop);
  // Audit 08 V3: pass chop-measured presence so the wind term floors at its residual
  // (chop already prices the wind's water effect) rather than double-counting it. The
  // forecast path (windWaveHeight null) keeps the full WIND_CURVE proxy.
  const fWind = swimWindFactor(input.windSpeed, input.windGusts, input.windWaveHeight !== null);
  const fCurrent = swimCurrentFactor(input.currentVelocity);

  const fWeather = swimWeatherFactor({
    cloudCoverPct: input.cloudCoverPct,
    precipitationMm: input.precipitation,
    visibilityM: input.visibilityM,
    airApparentTempC: input.airApparentTempC,
    weatherCode: input.weatherCode,
  });

  const fWaterQuality = swimWaterQualityFactor({
    secchiDepthM: input.secchiDepthM,
    chlorophyllA: input.chlorophyllA,
    spm: input.spm,
    bathingClassification: input.bathingClassification,
    csoActive: input.csoActive,
    hasCsoData: input.hasCsoData,
    trailingRain24h: input.trailingRain24h,
  });

  const raw = BASE_SCORE * composeScore(SWIM_CHAIN, {
    chop: fChop,
    wind: fWind,
    current: fCurrent,
    weather: fWeather,
    waterQuality: fWaterQuality,
  }).compressed;
  const finalScore = Math.round(clamp(raw, 0.0, 10.0) * 10) / 10;

  const significantWaveHeight =
    effectiveWindWave !== null
      ? Math.sqrt(effectiveSwell ** 2 + effectiveWindWave ** 2)
      : effectiveSwell;

  const safety = computeSwimSafety({
    currentVelocity: input.currentVelocity,
    windSpeed: input.windSpeed,
    windGusts: input.windGusts,
    significantWaveHeight,
    visibilityM: input.visibilityM,
    uvIndex: input.uvIndex,
    weatherCode: input.weatherCode,
    capeJkg: input.capeJkg,
    waterTempC: input.waterTempC,
    csoActive: input.csoActive,
    chlorophyllA: input.chlorophyllA,
    hasCsoData: input.hasCsoData,
    trailingRain24h: input.trailingRain24h,
  });

  return {
    score: finalScore,
    label: swimLabel(finalScore),
    factors: {
      chop: Math.round(fChop * 1000) / 1000,
      wind: Math.round(fWind * 1000) / 1000,
      current: Math.round(fCurrent * 1000) / 1000,
      weather: Math.round(fWeather * 1000) / 1000,
      waterQuality: Math.round(fWaterQuality * 1000) / 1000,
    },
    temperatureTag:
      input.waterTempC !== null ? swimTemperatureTag(input.waterTempC) : "Unknown",
    temperatureC: input.waterTempC,
    effectiveChop: Math.round(chop * 100) / 100,
    chopConfidence: input.windWaveHeight === null ? "inferred" : "full",
    safety,
  };
}

// ---------------------------------------------------------------------------
// Revision 2 (all-sports engine design 6.3; engines/coastal/r2/swim.ts). Revision 1 callers
// never reach these functions.
//
// - DIVSWIM-4: a sixth driver, the surf-zone passage factor (surf-zone.ts), for swims that
//   start from the beach at a surf break; 1.0 for offshore-route swims, which keep revision 1's
//   gentle-roller treatment of long-period swell beyond the break.
// - DIVSWIM-11: the satellite water-quality sub-scores average only the values the evidence
//   holds (revision 1 filled each missing one with a near-perfect default); with no satellite
//   evidence at all the satellite part takes the neutral 1.0 and the kernel marks the hour
//   partial, so the default is disclosed instead of presented as a reading. The trailing-rain
//   sewage proxy is unchanged.
// - Every input is resolved by the caller under the missing-data rule (design 2.5).
// ---------------------------------------------------------------------------

/** Swim revision 2's chain: revision 1's five factors plus the surf-zone passage driver (pinned SHORT gamma). */
export const SWIM_CHAIN_R2 = defineChain({
  sport: "swim-r2",
  gamma: GAMMA_SHORT_CHAIN, // Pinned: six condition factors stay SHORT; offshore-route swims equal revision 1.
  factors: [
    { key: "chop", class: "driver" },
    { key: "wind", class: "driver" },
    { key: "current", class: "driver" },
    { key: "weather", class: "modifier", note: "As SWIM_CHAIN." },
    { key: "waterQuality", class: "driver", note: "DIVSWIM-11: available satellite sub-scores only; neutral 1.0 without satellite evidence (partial hour)." },
    { key: "surfZone", class: "driver", note: "DIVSWIM-4: surf-zone passage for beach entry at a surf break; 1.0 for offshore routes." },
  ],
});

/**
 * Cloud cover (%) at which the sun sub-factor is 1.0 (between the 30% and 50% anchors):
 * revision 2's neutral for a missing cloud cover. Revision 1 read a missing value as 50%.
 */
export const SWIM_NEUTRAL_CLOUD_PCT = 100 / 3;

/** Revision 2 water quality: the mean of the satellite sub-scores the evidence holds (1.0 without any), times the sewage proxy. */
export function swimWaterQualityFactorR2(input: {
  secchiDepthM: number | null;
  chlorophyllA: number | null;
  spm: number | null;
  trailingRain24h: number;
}): number {
  const subs: number[] = [];
  if (input.secchiDepthM !== null) subs.push(piecewise(input.secchiDepthM, SECCHI_CURVE));
  if (input.chlorophyllA !== null) subs.push(piecewise(input.chlorophyllA, CHLOROPHYLL_CURVE));
  if (input.spm !== null) subs.push(piecewise(input.spm, SPM_CURVE));
  const satellite = subs.length === 0 ? 1 : subs.reduce((sum, value) => sum + value, 0) / subs.length;
  return satellite * piecewise(input.trailingRain24h, SEWAGE_PROXY_CURVE);
}

export type SwimHourlyR2Input = Readonly<{
  /** Primary swell height (m) before shelter. */
  swellHeight: number;
  /** Wind-sea height (m) before shelter; null when unmeasured (the wind curve then prices the wind sea, as revision 1). */
  windWaveHeight: number | null;
  swellPeriod: number;
  windSpeed: number;
  windGusts: number;
  currentVelocity: number;
  cloudCoverPct: number;
  precipitation: number;
  trailingRain24h: number;
  visibilityM: number;
  weatherCode: number;
  secchiDepthM: number | null;
  chlorophyllA: number | null;
  spm: number | null;
  shelterFactor: number;
  /** surf-zone.ts passageFactor for beach entry at a surf break; 1 for an offshore route. */
  surfZonePassage: number;
}>;

export type SwimHourlyR2Result = Readonly<{
  /** 0 to 10, one decimal as revision 1 rounds it. */
  score: number;
  factors: SwimFactors & Readonly<{ surfZone: number }>;
  effectiveChop: number;
}>;

export function calculateSwimHourlyR2(input: SwimHourlyR2Input): SwimHourlyR2Result {
  const effectiveSwell = input.swellHeight * input.shelterFactor;
  const effectiveWindWave = input.windWaveHeight !== null ? input.windWaveHeight * input.shelterFactor : null;
  const chop = swimEffectiveChop(effectiveSwell, effectiveWindWave, input.swellPeriod);
  const fChop = swimChopFactor(chop);
  const fWind = swimWindFactor(input.windSpeed, input.windGusts, input.windWaveHeight !== null);
  const fCurrent = swimCurrentFactor(input.currentVelocity);
  const fWeather = swimWeatherFactor({
    cloudCoverPct: input.cloudCoverPct,
    precipitationMm: input.precipitation,
    visibilityM: input.visibilityM,
    airApparentTempC: null,
    weatherCode: input.weatherCode,
  });
  const fWaterQuality = swimWaterQualityFactorR2({
    secchiDepthM: input.secchiDepthM,
    chlorophyllA: input.chlorophyllA,
    spm: input.spm,
    trailingRain24h: input.trailingRain24h,
  });
  const fSurfZone = clamp(input.surfZonePassage, 0, 1);
  const raw = BASE_SCORE * composeScore(SWIM_CHAIN_R2, {
    chop: fChop,
    wind: fWind,
    current: fCurrent,
    weather: fWeather,
    waterQuality: fWaterQuality,
    surfZone: fSurfZone,
  }).compressed;
  return {
    score: Math.round(clamp(raw, 0.0, 10.0) * 10) / 10,
    factors: { chop: fChop, wind: fWind, current: fCurrent, weather: fWeather, waterQuality: fWaterQuality, surfZone: fSurfZone },
    effectiveChop: chop,
  };
}

/**
 * Revision 2 daily score on 0 to 100 at native precision: revision 1's rollup (60% best 3-hour
 * window, 40% daylight mean) over the hours with a score, without the final rounding.
 */
export function deriveSwimDailyR2(
  hourlyScores: readonly { hour: number; score: number | null; isDay: boolean }[],
): Readonly<{ score: number | null; bestWindowStart: number | null; availableHours: number }> {
  const daily = summariseDailyQuality(hourlyScores, DAILY_BEST_WINDOW_HOURS, DAILY_BEST_WINDOW_WEIGHT);
  if (daily.availableHours === 0) return { score: null, bestWindowStart: null, availableHours: 0 };
  return { score: clamp(daily.overall * 10, 0, 100), bestWindowStart: daily.bestWindowStart, availableHours: daily.availableHours };
}

// ---------------------------------------------------------------------------
// Safety policy v2 (all-sports engine design 2.7 and 6.2; review DIVSWIM-4 safety part, WFD-6
// pattern). Used only by engines/coastal/safety/swim.ts for sports listed in
// CONDITIONS_SAFETY_POLICY_V2_SPORTS; computeSwimSafety above is unchanged.
//
// Differences from v1:
// - rip-current and shorebreak rows for swims that start from the beach at a surf break
//   (surf-zone.ts), unavailable when the sea state behind them is missing, or when only a lower
//   bound (the swell without its wind sea) is known and it shows no hazard;
// - the sustained-wind rows no longer sit behind the gust row, so a missing gust never hides
//   them; a missing gust is its own unavailable row unless the sustained wind already shows
//   the red gust level;
// - a missing weather code gives "Storm forecast unavailable", a missing CAPE "Thunder risk
//   unavailable" (unless the code already says thunderstorm), and missing wind, sea state, UV
//   index or water temperature give unavailable rows (revision 1 read a missing UV index and
//   CAPE as 0 and a missing water temperature as no row);
// - evidence-gated advisories stay evidence-gated: the algae rows need a chlorophyll reading and
//   the rainfall-runoff row a trailing-rain reading; without them there is no row (the missing
//   water quality is disclosed on the score side as a partial hour).
// ---------------------------------------------------------------------------

export type SwimEntry = "beach" | "offshore-route";

export type SwimSafetyV2Input = Readonly<{
  currentVelocity: number | null;
  windSpeed: number | null;
  windGusts: number | null;
  /** Significant wave height after shelter (m), null when not exactly known. */
  significantWaveHeight: number | null;
  /** A lower bound on it when only one partition is known. */
  significantWaveHeightMin: number | null;
  visibilityM: number | null;
  /**
   * The hour's relative humidity in % (model-fog.ts), the visibility row's saturation test
   * (accelerated rollout Phase E). Null or omitted when unknown: the air counts as saturated, so a
   * missing humidity never lowers the row.
   */
  relativeHumidityPct?: number | null;
  uvIndex: number | null;
  weatherCode: number | null;
  capeJkg: number | null;
  waterTempC: number | null;
  chlorophyllA: number | null;
  trailingRain24h: number | null;
  entry: SwimEntry;
  /**
   * Beach entry only: the surf-zone hazards, or null when the sea state behind them is missing.
   * `exact` is false when the wind sea is unknown (neither measured nor recoverable), so the
   * hazards come from the swell alone and are a lower bound: a hazard they show is shown, but "none"
   * is not clear.
   */
  surfZone: Readonly<{ rip: "none" | "moderate" | "high"; shorebreak: "none" | "moderate" | "high"; breakerHeightM: number; exact: boolean }> | null;
}>;

export type SwimSafetyV2Row = Readonly<{
  key: string;
  severity: "amber" | "red" | "unavailable";
  label: string;
  detail: string;
}>;

export function computeSwimSafetyV2(input: SwimSafetyV2Input): SwimSafetyV2Row[] {
  const rows: SwimSafetyV2Row[] = [];
  const unavailable = (key: string, label: string, detail: string) => rows.push({ key, severity: "unavailable", label, detail });

  const current = input.currentVelocity;
  if (current === null) {
    unavailable("current-unavailable", "Current data unavailable", "The current forecast is missing, so drift risk cannot be assessed.");
  } else if (current > SAFETY_CURRENT_RED_MS) {
    rows.push({ key: "current", severity: "red", label: "Strong current", detail: `Current ${current.toFixed(1)} m/s. Risk of being swept into shipping lanes or out to sea. Do not swim.` });
  } else if (current > SAFETY_CURRENT_AMBER_MS) {
    rows.push({ key: "current", severity: "amber", label: "Moderate current", detail: `Current ${current.toFixed(1)} m/s. Swim parallel to shore and stay within your depth.` });
  }

  const wave = input.significantWaveHeight ?? input.significantWaveHeightMin;
  if (wave !== null && wave > SAFETY_WAVE_RED_M) {
    rows.push({ key: "waves", severity: "red", label: "Dangerous waves", detail: `Wave height ${wave.toFixed(1)}m. Not safe for open water swimming.` });
  } else if (wave !== null && wave > SAFETY_WAVE_AMBER_M) {
    rows.push({ key: "waves", severity: "amber", label: "Large waves", detail: `Wave height ${wave.toFixed(1)}m. Experienced swimmers only. Consider sheltered alternatives.` });
  } else if (input.significantWaveHeight === null) {
    unavailable("waves-unavailable", "Sea-state data unavailable", "The swell or wind-sea forecast is missing, so wave height cannot be assessed.");
  }

  if (input.entry === "beach") {
    const zone = input.surfZone;
    if (zone === null) {
      unavailable("surf-zone-unavailable", "Surf-zone data unavailable", "The swell forecast is missing, so rip currents and shorebreak at the beach cannot be assessed.");
    } else {
      const breaker = zone.breakerHeightM.toFixed(1);
      if (zone.rip === "high") {
        rows.push({ key: "rip", severity: "red", label: "Rip currents likely", detail: `Waves of about ${breaker}m breaking at the beach drive strong rip currents. Do not swim from the beach.` });
      } else if (zone.rip === "moderate") {
        rows.push({ key: "rip", severity: "amber", label: "Rip current risk", detail: `Waves of about ${breaker}m breaking at the beach can drive rip currents. Swim at a lifeguarded beach, between the flags.` });
      }
      if (zone.shorebreak === "high") {
        rows.push({ key: "shorebreak", severity: "red", label: "Heavy shorebreak", detail: `Waves of about ${breaker}m dump on the shore. Entry and exit are dangerous.` });
      } else if (zone.shorebreak === "moderate") {
        rows.push({ key: "shorebreak", severity: "amber", label: "Shorebreak", detail: `Waves of about ${breaker}m dump close to the shore. Time entry and exit between sets.` });
      }
      // A lower bound that shows no hazard is not clear (as the waves row above).
      if (!zone.exact && zone.rip === "none" && zone.shorebreak === "none") {
        unavailable("surf-zone-unavailable", "Surf-zone data unavailable", "The wind-sea forecast is missing, so rip currents and shorebreak at the beach cannot be fully assessed.");
      }
    }
  }

  // Gusts and sustained wind are assessed separately; sustained never exceeds its gust.
  const wind = input.windSpeed;
  const gust = input.windGusts;
  const gustBound = gust ?? wind;
  if (gustBound !== null && gustBound > SAFETY_GUST_RED_KMH) {
    rows.push({ key: "gusts", severity: "red", label: "Dangerous gusts", detail: `Gusts ${Math.round(gustBound)} km/h. Risk of being pushed offshore.` });
  } else if (gust === null) {
    unavailable("gust-unavailable", "Gust data unavailable", "The gust forecast is missing, so offshore push cannot be assessed.");
  } else if (gust > SAFETY_GUST_AMBER_KMH) {
    rows.push({ key: "gusts", severity: "amber", label: "Strong gusts", detail: `Gusts ${Math.round(gust)} km/h. Stay close to shore.` });
  }
  if (wind === null) {
    if (!(gust !== null && gust <= SAFETY_WIND_AMBER_KMH)) {
      unavailable("wind-unavailable", "Wind data unavailable", "The wind forecast is missing, so drift and surface disturbance cannot be assessed.");
    }
  } else if (wind > SAFETY_WIND_RED_KMH) {
    rows.push({ key: "wind", severity: "amber", label: "Strong wind", detail: `Wind ${Math.round(wind)} km/h. Stay close to shore.` });
  } else if (wind > SAFETY_WIND_AMBER_KMH) {
    rows.push({ key: "wind", severity: "amber", label: "Strong wind", detail: `Wind ${Math.round(wind)} km/h. Consider sheltered alternatives.` });
  }

  // Model fog (model-fog.ts, accelerated rollout Phase E): red only in saturated air, at swim's own 300 m.
  const vis = input.visibilityM;
  const fog = classifyModelFog({ visibilityM: vis, relativeHumidityPct: input.relativeHumidityPct }, SAFETY_VIS_RED_M);
  if (fog === "unavailable") {
    unavailable("visibility-unavailable", "Visibility data unavailable", "The visibility forecast is missing, so sighting and support-craft awareness cannot be assessed.");
  } else if (fog === "red") {
    rows.push({ key: "visibility", severity: "red", label: "Dense fog", detail: `Visibility ${Math.round(vis as number)}m. Cannot see shore, buoys, or support craft. Do not swim.` });
  } else if (fog === "amber") {
    rows.push({ key: "visibility", severity: "amber", label: "Reduced visibility", detail: `Visibility ${Math.round(vis as number)}m. Stay close to shore and swim with a buddy.` });
  }

  const code = input.weatherCode;
  if (code === null) {
    unavailable("storm-unavailable", "Storm forecast unavailable", "The weather code is missing, so thunderstorm risk cannot be assessed.");
  } else if (THUNDERSTORM_CODES.has(code)) {
    rows.push({ key: "lightning", severity: "red", label: "Thunderstorm", detail: "Lightning is extremely dangerous in open water. Do not swim. Seek shelter immediately." });
  } else if (input.capeJkg === null) {
    unavailable("thunder-unavailable", "Thunder risk unavailable", "Convective energy (CAPE) is missing, so thunderstorm risk cannot be fully assessed.");
  } else if (input.capeJkg > SAFETY_CAPE_RED) {
    rows.push({ key: "lightning", severity: "red", label: "Thunderstorm risk", detail: "High convective energy indicates thunderstorm risk. Monitor conditions and stay close to exit points." });
  } else if (input.capeJkg > SAFETY_CAPE_AMBER && CONVECTIVE_RAIN_CODES.has(code)) {
    rows.push({ key: "lightning", severity: "amber", label: "Thunderstorms possible", detail: "Convective rain with moderate instability. Stay close to shore and monitor conditions." });
  }

  const chla = input.chlorophyllA;
  if (chla !== null && chla > 30) {
    rows.push({ key: "algae", severity: "red", label: "Algae bloom", detail: "Very high chlorophyll levels indicate harmful algal bloom risk. Avoid water contact." });
  } else if (chla !== null && chla > SAFETY_CHLOROPHYLL_BLOOM) {
    rows.push({ key: "algae", severity: "amber", label: "Algae bloom risk", detail: "Elevated chlorophyll levels. Monitor local water quality advisories." });
  }

  const uv = input.uvIndex;
  if (uv === null) {
    unavailable("uv-unavailable", "UV data unavailable", "The UV forecast is missing, so sun exposure cannot be assessed.");
  } else if (uv > SAFETY_UV_RED) {
    rows.push({ key: "uv", severity: "amber", label: `High UV (${Math.round(uv)})`, detail: "Extended open water exposure with no shade. Wear SPF 50+ waterproof sunscreen." });
  } else if (uv > SAFETY_UV_AMBER) {
    rows.push({ key: "uv", severity: "amber", label: `Moderate UV (${Math.round(uv)})`, detail: "Apply waterproof sunscreen regularly." });
  }

  const water = input.waterTempC;
  if (water === null) {
    unavailable("water-temp-unavailable", "Water temperature unavailable", "The sea temperature is missing, so cold-water risk cannot be assessed.");
  } else if (water < SAFETY_COLD_WATER_C) {
    const extremeCold = water < 5;
    const coldShock = water < SAFETY_COLD_SHOCK_C;
    rows.push({
      key: "cold-water",
      severity: extremeCold ? "red" : "amber",
      label: extremeCold ? "Extreme cold water" : coldShock ? "Cold water shock risk" : "Cold water guidance",
      detail: extremeCold
        ? `Water ${Math.round(water)}C. Hypothermia risk even with wetsuit. Experienced cold water swimmers only.`
        : coldShock
          ? `Water ${Math.round(water)}C. Risk of cold water shock on entry. Acclimatise gradually and wear appropriate neoprene.`
          : `Water ${Math.round(water)}C. Cold-water exposure starts below 15C. Acclimatise gradually, use suitable kit and plan a warm exit.`,
    });
  }

  const rain = input.trailingRain24h;
  if (rain !== null && rain > 12) {
    rows.push({ key: "rainfall-runoff", severity: "amber", label: "Rainfall warning", detail: `${Math.round(rain)}mm rain in 24h. Possible elevated bacteria from runoff and overflows.` });
  }

  return rows;
}

// ---------------------------------------------------------------------------
// Daily rollup
// ---------------------------------------------------------------------------

export function deriveSwimDaily(
  hourlyScores: { hour: number; score: number; isDark: boolean }[],
): { overall: number; bestWindowStart: number | null; bestWindowScores: number[] } {
  const daily = summariseDailyQuality(
    hourlyScores.map((point) => ({ ...point, isDay: !point.isDark })),
    DAILY_BEST_WINDOW_HOURS, DAILY_BEST_WINDOW_WEIGHT,
  );
  return {
    overall: Math.round((daily.overall) * 10) / 10,
    bestWindowStart: daily.bestWindowStart,
    bestWindowScores: daily.bestWindowScores,
  };
}

// ---------------------------------------------------------------------------
// Legacy compat exports (used by older call sites during migration)
// ---------------------------------------------------------------------------

export function swimWavesPenalty(chop: number): number {
  return swimChopFactor(chop);
}

export function swimWindPenalty(windSpeedKmh: number): number {
  return piecewise(windSpeedKmh, WIND_CURVE);
}

export function swimCurrentPenalty(velocityMs: number): number {
  return swimCurrentFactor(velocityMs);
}

export function swimVisibilityPenalty(secchiDepthM: number | null): number {
  return piecewise(secchiDepthM ?? 4.5, SECCHI_CURVE);
}

export function swimWaterQualityPenalty(
  chlorophyllA: number | null,
  spm: number | null
): number {
  const chlaSub = piecewise(chlorophyllA ?? 4.0, CHLOROPHYLL_CURVE);
  const spmSub = piecewise(spm ?? 5.0, SPM_CURVE);
  return (chlaSub + spmSub) / 2;
}

export function swimPrecipitationPenalty(
  precipitationMm: number,
  trailingRain24h: number,
  hasCsoData: boolean
): number {
  const rainSub = piecewise(precipitationMm, RAIN_CURVE);
  let sewageSub = 1.0;
  if (!hasCsoData) {
    sewageSub = piecewise(trailingRain24h, SEWAGE_PROXY_CURVE);
  }
  return rainSub * sewageSub;
}


export { isDark };
export type { SunTimes };
